import { useEffect } from 'react'
import { Atrium, PenIcon, TrashIcon, countOf, useToast } from '@ofself/shell'
import { useChat } from '../contexts/ChatContext'
import useWorldMove from './useWorldMove'
import { ALL, UNSORTED, inWorld, worldName, worldRooms } from '../utils/worlds'

/* THE ATRIUM, Synapse's home: every world of the group at once, as the shell's
   periodic table (<Atrium>, ShellKit's Atrium on the Mac: one atrium for every
   app). This file only says what a world is and what its verbs do.

   A WORLD IS A LENS over the group's channels (utils/worlds.js): All channels
   stands apart as element 00; Unsorted (the channels in no world) heads the
   table, kept, never ranked; then every world in the group's order, which the
   tiles drag into and every member's atrium shares. The plus founds a world and
   steps into it; a right-click opens, renames in place or removes one (its
   channels stay). Channels carried here from the column move into the world. */

const counted = (n) => countOf(n, 'channel')

export default function WorldsAtrium({ onEnter }) {
  const { activeGroup, groups, selectGroup, worldsByGroup, groupChannelsByGroup, activeWorld, enterWorld,
    createWorld, renameWorld, deleteWorld, reorderWorlds } = useChat()
  const toast = useToast()
  const { drop } = useWorldMove()
  // home is a group's atrium: from Personal (no worlds) it stands in the group you were last in
  useEffect(() => {
    if (activeGroup || !groups?.length) return
    let last = null
    try { last = localStorage.getItem('synapse_last_group') } catch {}
    selectGroup(groups.find((g) => g.id === last) || groups[0])
  }, [activeGroup, groups, selectGroup])
  useEffect(() => { if (activeGroup) try { localStorage.setItem('synapse_last_group', activeGroup.id) } catch {} }, [activeGroup])
  if (!activeGroup) return null

  const gid = activeGroup.id
  const worlds = worldsByGroup[gid] || []
  const channels = groupChannelsByGroup[gid] || []
  const count = (id) => channels.filter((c) => inWorld(c, id, worlds)).length
  const room = (id, extra) => ({ id, name: worldName(id, worlds), count: count(id), meta: counted(count(id)), ...extra })
  const rooms = [
    room(ALL, { master: true, symbol: 'All', help: 'Every channel of the group, through no lens. Press it to read them all.' }),
    ...worldRooms(worlds).map((id) => id === UNSORTED
      ? room(id, { symbol: 'Un', help: 'The channels in no world, so none is ever lost. Carry channels here to take them out of every world.' })
      : room(id, { help: 'A world: a subject the group’s channels gather under. Press it to read its channels; drag it into the group’s order; right-click to rename or remove it.' })),
  ]
  const open = (id) => { enterWorld(gid, id); onEnter?.() }

  const mint = async (name) => {
    const w = await createWorld(gid, name)
    if (w) open(w.id)
    else toast(`could not make the world “${name}”`, { tone: 'danger' })
  }
  // THE FINDER'S VERBS (Unsorted is kept, not made: it only opens)
  const menu = (r, { rename }) => r.id === UNSORTED ? [] : [
    { label: 'Open', onSelect: () => open(r.id) },
    { icon: <PenIcon size={16} />, label: 'Rename…', onSelect: rename },
    { separator: true },
    { icon: <TrashIcon size={16} />, label: 'Remove…', danger: true, onSelect: () => {
      if (window.confirm(`Remove the world “${r.name}”? Its channels stay, in any other worlds they stand in, or in Unsorted.`)) deleteWorld(gid, r.id)
    } },
  ]

  return (
    <Atrium title="Worlds" masterTitle={activeGroup.name} rooms={rooms} activeId={activeWorld}
      onOpen={(r) => open(r.id)} onMint={mint} mintLabel="New world"
      mintHelp="Founds a world by name and steps into it. Carry channels onto it, or Move to… from their right-click."
      onRename={(id, name) => renameWorld(gid, id, name)} menu={menu}
      // the worlds' ids in their new order (All channels and Unsorted are kept, never ranked)
      onReorder={(ids) => reorderWorlds(gid, ids.filter((id) => id !== ALL && id !== UNSORTED))}
      onDropItems={drop} />
  )
}
